import { Injectable } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ParamMap } from '@angular/router';
import { DeviceAspectRatioType } from '@models/enums/device-aspect-ratio-type.enum';
import { DistanceType, Measurements } from '@models/measurements';
import { OtherUserPreferences } from '@models/other-user-preferences';
import { UserPreferences } from '@models/user-preferences';
import { UserProfile } from '@models/user-profile';
import { Logger } from '@services/logging.service';
import { WebAppService } from '@services/xhr/web-app-svc';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, shareReplay, tap, timeout } from 'rxjs/operators';

@Injectable()
export class UserService {
  user: UserProfile;
  userServiceStatus$ = new BehaviorSubject('loading');
  userThemes = {
    'Dark Theme': 'dark-theme',
    'Light Theme': 'light-theme',
  };
  userTypes = {
    Default: null || 'null',
    Tech: 'TECH',
    Manager: 'TECH_SUPER',
  };
  measurementOptions = {
    distances: {
      Kilometer: 'km',
      Kilofoot: 'kft',
      Miles: 'ml'
    }
  };

  userPreferences$ = new BehaviorSubject(new UserPreferences());

  constructor(
    private sanitizer: DomSanitizer,
    private webAppService: WebAppService
  ) {

  }

  getUserProfile(): Observable<UserProfile> {
    return this.webAppService.profile()
      .pipe(
        tap(user => {
          if (user) {
            this.user = user;
              if ( this.user != null && this.user.roles?.includes('LIMITED_ACCESS') && !this.user.roles?.includes('FULL_ACCESS')) {
                localStorage.setItem('ROLE', "LIMITED");
              }
              else {
                localStorage.setItem('ROLE', "FULL");
              }
          }
        }),
        shareReplay(),
        timeout(10000),
        catchError(err => {
          if (err.status !== 403 && err.status !== 401) {
            this.userServiceStatus$.next('broken');
          }
          throw err;
        })
      );
  }

  fetchProfilePreferences(): Observable<UserPreferences> {
    const obs = this.webAppService.preferencesGet().pipe(shareReplay());
    obs.subscribe(prefs => {
      if (!prefs.theme) {
        prefs.theme = 'light-theme';
      }
      if (!prefs.otherPrefs) {
        prefs.otherPrefs = new OtherUserPreferences();
      }
      if (!prefs.otherPrefs?.measurements) {
        let measurements: Measurements = new Measurements();
        prefs.otherPrefs.measurements = measurements;
      }
      this.userPreferences$.next(prefs);
    });
    return obs;
  }

  saveProfilePreferences(): void {
    this.webAppService.preferencesPut(this.userPreferences$.getValue())
      .subscribe(prefs => {
        this.userPreferences$.next(prefs);
      });
  }

  saveTheme(theme: string): void {
    const newPrefs = this.userPreferences$.getValue();
    newPrefs.theme = theme;
    this.userPreferences$.next(newPrefs);
    this.webAppService.preferencesPut(newPrefs).subscribe();
  }
  saveDistance(distance: DistanceType): void {
    const newPrefs = this.userPreferences$.getValue();
    newPrefs.otherPrefs.measurements.distance = distance;
    this.webAppService.preferencesPut(newPrefs).subscribe(prefs => {
      this.userPreferences$.next(prefs);
      window.location.reload();
    });
  }
  saveOtherPref(pref: keyof typeof DeviceAspectRatioType): void {
    const newPrefs = this.userPreferences$.getValue();
    newPrefs.otherPrefs = {
      aspectRatio: pref,
      measurements: this.userPreferences$.getValue().otherPrefs.measurements
    };
    this.userPreferences$.next(newPrefs);
    this.webAppService.preferencesPut(newPrefs).subscribe();
  }

  getUserProfileByUsername(username: string): Observable<UserProfile> {
    return this.webAppService.profile(username);
  }

  uploadProfileImage(userImg: any): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', userImg, userImg.name);
    return this.webAppService.profileImageUpload(formData);
  }

  /**
   * Get nt user name from query parameter
   * If no user name is provided in a query parameter, default to the user logged into the app
   * @returns string value containing nt user name
   */
  getTargetUser(targetProfile: UserProfile, params: ParamMap): string {
    return params.has('username') ? params.get('username') as string : targetProfile.employee.username;
  }

  getProfileImageStyle(profilePhoto: string): SafeStyle {
    if (profilePhoto) {
      const style = `background-image: url(${profilePhoto})`;
      // return style;
      return this.sanitizer.bypassSecurityTrustStyle(style);
    } else {
      Logger.warn('no photo');
      return '';
    }
  }

  checkPrivilege(privilege: any): boolean {
    if (this.user && this.user.privileges) {
      // @ts-ignore
      return this.user.privileges[privilege];
    } else {
      return false;
    }
  }


}




